"use client";

/* eslint-disable react/prop-types */
import { useState, useEffect } from "react";
import { X } from "lucide-react";

const DEFAULT_LOCALE = "ko";

const AUTH_T = {
  login: { ko: "로그인", en: "Log in", ja: "ログイン", zh: "登录", th: "เข้าสู่ระบบ" },
  signup: { ko: "회원가입", en: "Sign up", ja: "会員登録", zh: "注册", th: "สมัครสมาชิก" },
  name: { ko: "이름", en: "Name", ja: "名前", zh: "姓名", th: "ชื่อ" },
  birthdate: { ko: "생년월일 (생일·출생 연도)", en: "Date of birth", ja: "生年月日", zh: "出生日期", th: "วันเกิด" },
  phone: { ko: "휴대폰 번호", en: "Mobile phone", ja: "携帯番号", zh: "手机号码", th: "เบอร์มือถือ" },
  email: { ko: "이메일", en: "Email", ja: "メール", zh: "邮箱", th: "อีเมล" },
  password: { ko: "비밀번호", en: "Password", ja: "パスワード", zh: "密码", th: "รหัสผ่าน" },
  passwordConfirm: {
    ko: "비밀번호 확인",
    en: "Confirm password",
    ja: "パスワード確認",
    zh: "确认密码",
    th: "ยืนยันรหัสผ่าน",
  },
  or: { ko: "또는", en: "or", ja: "または", zh: "或", th: "หรือ" },
  needAccount: {
    ko: "아직 계정이 없으신가요?",
    en: "Don’t have an account?",
    ja: "アカウントをお持ちでない方",
    zh: "还没有账号？",
    th: "ยังไม่มีบัญชีใช่ไหม?",
  },
  haveAccount: {
    ko: "이미 계정이 있으신가요?",
    en: "Already have an account?",
    ja: "すでにアカウントをお持ちですか？",
    zh: "已经有账号？",
    th: "มีบัญชีอยู่แล้วใช่ไหม?",
  },
  privacyTitle: {
    ko: "개인정보 수집·이용 동의 (필수)",
    en: "Consent to collection and use of personal information (required)",
    ja: "個人情報の収集・利用への同意（必須）",
    zh: "个人信息收集·使用同意（必填）",
    th: "การยินยอมให้เก็บและใช้ข้อมูลส่วนบุคคล (จำเป็น)",
  },
  privacyItems: {
    ko: "수집 항목(모두 필수): 이름, 생일, 출생 연도, 휴대폰 번호(전화번호), 이메일",
    en: "Items collected: name, date of birth, mobile number, email",
    ja: "収集項目：氏名、生年月日、携帯番号、メール",
    zh: "收集项目：姓名、出生日期、手机号、邮箱",
    th: "รายการที่เก็บ: ชื่อ, วันเกิด, เบอร์มือถือ, อีเมล",
  },
  privacyPurpose: {
    ko: "이용 목적: 회원가입, 본인확인, 서비스 제공",
    en: "Purpose: sign-up, identity verification, service provision",
    ja: "利用目的：会員登録、本人確認、サービス提供",
    zh: "使用目的：注册、身份验证、提供服务",
    th: "วัตถุประสงค์: สมัครสมาชิก, ยืนยันตัวตน, ให้บริการ",
  },
  privacyPeriod: {
    ko: "보유 기간: 회원 탈퇴 시까지",
    en: "Retention period: until account withdrawal",
    ja: "保有期間：退会時まで",
    zh: "保留期限：至注销为止",
    th: "ระยะเวลาจัดเก็บ: จนกว่าจะยกเลิกการเป็นสมาชิก",
  },
  privacyAgree: {
    ko: "위 개인정보 수집·이용에 동의합니다.",
    en: "I agree to the collection and use of the personal information above.",
    ja: "上記の個人情報の収集・利用に同意します。",
    zh: "我同意上述个人信息的收集和使用。",
    th: "ยอมรับการเก็บและใช้ข้อมูลส่วนบุคคลข้างต้น",
  },
  privacyRequired: {
    ko: "개인정보 수집·이용에 동의해주세요.",
    en: "Please agree to the collection and use of personal information.",
    ja: "個人情報の収集・利用に同意してください。",
    zh: "请同意个人信息的收集和使用。",
    th: "กรุณายอมรับการเก็บและใช้ข้อมูลส่วนบุคคล",
  },
  privacyPolicyLink: {
    ko: "개인정보처리방침 전문 보기",
    en: "View full privacy policy",
    ja: "個人情報処理方針の全文を見る",
    zh: "查看隐私政策全文",
    th: "ดูนโยบายความเป็นส่วนตัวฉบับเต็ม",
  },
};

function getLocaleString(value, locale) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  return value?.[locale] ?? value?.[DEFAULT_LOCALE] ?? Object.values(value || {})[0] ?? "";
}

const t = (obj, locale) => getLocaleString(obj, locale);

export function AuthModal({
  open,
  onClose,
  initialTab = "login",
  locale = "ko",
  onLoginSubmit,
  onSignupSubmit,
}) {
  const [tab, setTab] = useState(initialTab);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);

  useEffect(() => setTab(initialTab), [initialTab]);

  useEffect(() => {
    if (tab !== "signup") setPrivacyAgreed(false);
  }, [tab]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const handleSignupSubmit = (e) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const name = String(fd.get("name") || "").trim();
    const birthdate = String(fd.get("birthdate") || "").trim();
    const phone = String(fd.get("phone") || "").trim();
    const email = String(fd.get("email") || "").trim();
    const password = String(fd.get("password") || "");
    const passwordConfirm = String(fd.get("passwordConfirm") || "");

    const phoneOk = /^[0-9\-+() ]{7,20}$/.test(phone);
    if (!phoneOk) {
      alert("휴대폰 번호 형식을 확인해주세요.");
      return;
    }
    if (password.length < 8) {
      alert("비밀번호는 8자 이상이어야 합니다.");
      return;
    }
    if (password !== passwordConfirm) {
      alert("비밀번호가 일치하지 않습니다.");
      return;
    }
    if (!privacyAgreed) {
      alert(t(AUTH_T.privacyRequired, locale));
      return;
    }

    if (onSignupSubmit) {
      onSignupSubmit({ name, birthdate, phone, email, password });
    } else {
      // 입력값을 로그에 남기지 않는다 (#1032). 이름·생년월일·전화·이메일이
      // 그대로 찍히고 있었고, onSignupSubmit 을 넘기는 곳이 없어 늘 이 경로였다.
      alert("회원가입 요청이 전송되었습니다. (데모)");
    }
    onClose?.();
  };

  const handleLoginSubmit = (e) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const email = String(fd.get("email") || "").trim();
    const password = String(fd.get("password") || "");

    if (onLoginSubmit) {
      onLoginSubmit({ email, password });
    } else {
      // 이메일도 로그에 남기지 않는다 (#1032).
      alert("로그인 요청이 전송되었습니다. (데모)");
    }
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
        {/* 헤더 */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex gap-2">
            <button
              className={`rounded-full px-3 py-1 text-sm ${tab === "login" ? "bg-black text-white" : "bg-gray-100 text-gray-700"
                }`}
              onClick={() => setTab("login")}
            >
              {t(AUTH_T.login, locale)}
            </button>
            <button
              className={`rounded-full px-3 py-1 text-sm ${tab === "signup" ? "bg-black text-white" : "bg-gray-100 text-gray-700"
                }`}
              onClick={() => setTab("signup")}
            >
              {t(AUTH_T.signup, locale)}
            </button>
          </div>
          <button onClick={onClose} aria-label="Close">
            <X />
          </button>
        </div>

        {/* 바디 */}
        <div className="px-6 pb-6 pt-4">
          {tab === "login" ? (
            <form className="space-y-3" onSubmit={handleLoginSubmit}>
              <label className="block text-sm">
                {t(AUTH_T.email, locale)}
                <input
                  type="email"
                  name="email"
                  required
                  autoComplete="email"
                  className="mt-1 w-full rounded-lg border px-3 py-2 outline-none focus:ring"
                  placeholder="name@example.com"
                />
              </label>
              <label className="block text-sm">
                {t(AUTH_T.password, locale)}
                <input
                  type="password"
                  name="password"
                  required
                  autoComplete="current-password"
                  className="mt-1 w-full rounded-lg border px-3 py-2 outline-none focus:ring"
                  placeholder="••••••••"
                />
              </label>

              <button type="submit" className="mt-1 w-full rounded-lg bg-black px-4 py-2 text-white">
                {t(AUTH_T.login, locale)}
              </button>

              {/* 구분선 */}
              <div className="my-2 flex items-center gap-2 text-xs text-gray-400">
                <div className="h-px flex-1 bg-gray-200" />
                {t(AUTH_T.or, locale)}
                <div className="h-px flex-1 bg-gray-200" />
              </div>

              <div className="pt-2 text-center text-xs text-gray-600">
                {t(AUTH_T.needAccount, locale)}{" "}
                <button type="button" className="underline" onClick={() => setTab("signup")}>
                  {t(AUTH_T.signup, locale)}
                </button>
              </div>
            </form>
          ) : (
            <form className="space-y-3" onSubmit={handleSignupSubmit}>
              <p className="text-xs text-rose-500">* 표시는 필수 입력 항목입니다.</p>
              <label className="block text-sm">
                {t(AUTH_T.name, locale)}<span className="text-rose-500"> *</span>
                <input
                  type="text"
                  name="name"
                  required
                  autoComplete="name"
                  className="mt-1 w-full rounded-lg border px-3 py-2 outline-none focus:ring"
                />
              </label>

              <label className="block text-sm">
                {t(AUTH_T.birthdate, locale)}<span className="text-rose-500"> *</span>
                <input
                  type="date"
                  name="birthdate"
                  required
                  autoComplete="bday"
                  className="mt-1 w-full rounded-lg border px-3 py-2 outline-none focus:ring"
                />
              </label>

              <label className="block text-sm">
                {t(AUTH_T.phone, locale)}<span className="text-rose-500"> *</span>
                <input
                  type="tel"
                  name="phone"
                  required
                  inputMode="tel"
                  autoComplete="tel"
                  pattern="^[0-9\-+() ]{7,20}$"
                  title="휴대폰 번호를 입력하세요 (숫자와 -, (), 공백 허용)"
                  className="mt-1 w-full rounded-lg border px-3 py-2 outline-none focus:ring"
                  placeholder="010-1234-5678"
                />
              </label>

              <label className="block text-sm">
                {t(AUTH_T.email, locale)}<span className="text-rose-500"> *</span>
                <input
                  type="email"
                  name="email"
                  required
                  autoComplete="email"
                  className="mt-1 w-full rounded-lg border px-3 py-2 outline-none focus:ring"
                  placeholder="name@example.com"
                />
              </label>

              <label className="block text-sm">
                {t(AUTH_T.password, locale)}<span className="text-rose-500"> *</span>
                <input
                  type="password"
                  name="password"
                  required
                  autoComplete="new-password"
                  minLength={8}
                  className="mt-1 w-full rounded-lg border px-3 py-2 outline-none focus:ring"
                  placeholder="••••••••"
                />
              </label>

              <label className="block text-sm">
                {t(AUTH_T.passwordConfirm, locale)}<span className="text-rose-500"> *</span>
                <input
                  type="password"
                  name="passwordConfirm"
                  required
                  autoComplete="new-password"
                  minLength={8}
                  className="mt-1 w-full rounded-lg border px-3 py-2 outline-none focus:ring"
                  placeholder="••••••••"
                />
              </label>

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600">
                <p className="font-semibold text-gray-800">{t(AUTH_T.privacyTitle, locale)}</p>
                <ul className="mt-1 space-y-0.5">
                  <li>{t(AUTH_T.privacyItems, locale)}</li>
                  <li>{t(AUTH_T.privacyPurpose, locale)}</li>
                  <li>{t(AUTH_T.privacyPeriod, locale)}</li>
                </ul>
                <label className="mt-2 flex items-start gap-2 text-gray-800">
                  <input
                    type="checkbox"
                    name="privacyAgreed"
                    checked={privacyAgreed}
                    onChange={(e) => setPrivacyAgreed(e.target.checked)}
                    required
                    className="mt-0.5"
                  />
                  <span>{t(AUTH_T.privacyAgree, locale)}</span>
                </label>
                <a
                  href={locale === "ko" ? "/policy/private" : `/${locale}/policy/private`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 inline-block text-gray-500 underline"
                >
                  {t(AUTH_T.privacyPolicyLink, locale)}
                </a>
              </div>

              <button type="submit" className="mt-1 w-full rounded-lg bg-black px-4 py-2 text-white">
                {t(AUTH_T.signup, locale)}
              </button>

              {/* 구분선 */}
              <div className="my-2 flex items-center gap-2 text-xs text-gray-400">
                <div className="h-px flex-1 bg-gray-200" />
                {t(AUTH_T.or, locale)}
                <div className="h-px flex-1 bg-gray-200" />
              </div>

              <div className="pt-2 text-center text-xs text-gray-600">
                {t(AUTH_T.haveAccount, locale)}{" "}
                <button type="button" className="underline" onClick={() => setTab("login")}>
                  {t(AUTH_T.login, locale)}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
